/**
 * Surviving a deploy that happens while somebody is using the app.
 *
 * ── The bug ───────────────────────────────────────────────────────────────
 *
 * Every screen in `/v2` is a dynamic `import()`, and Vite fingerprints each
 * chunk — `index-CW5vOBl-.js`. Deploying replaces the whole set with new
 * names and deletes the old ones. A page that was loaded before the deploy is
 * still holding the OLD names, so the next screen change fetches a file that
 * no longer exists:
 *
 *     TypeError: Failed to fetch dynamically imported module
 *
 * The promise rejects, `renderRoute` never mounts anything, and NOTHING
 * HAPPENS. No error on screen, no spinner, no clue. Seen on staging on
 * 18 Aug 2026: an account was created successfully and the app simply refused
 * to move, which read as "the button is broken".
 *
 * It is not a staging quirk and it is not rare. It hits every advisor who has
 * the app open at the moment of a deploy, and it will be reported as "the app
 * stopped working" with nothing reproducible attached.
 *
 * ── The fix, and its one danger ───────────────────────────────────────────
 *
 * Reload once. The reload fetches the new `index.html`, which names the new
 * chunks, and everything is correct again.
 *
 * The danger is obvious: if the chunk is missing for any OTHER reason — a
 * broken deploy, a CDN fault, an offline device — reloading on failure is an
 * infinite refresh loop, which is far worse than a dead button. So a reload
 * is allowed at most once in a short window, recorded in `sessionStorage`.
 * If it happens again immediately, the error is left to surface honestly.
 */

/** How long a reload counts as "we already tried that". */
export const RELOAD_WINDOW_MS = 15000

/** Where the last attempt is recorded. Session-scoped: a new tab may retry. */
export const RELOAD_KEY = 'mbm_stale_bundle_reload'

/**
 * Is this the error a deleted chunk produces?
 *
 * Matched on the message rather than the type, because every browser throws
 * something different: Chrome and Safari throw `TypeError`, Firefox throws an
 * `Error`, and the wording differs in all three. Matching the type alone
 * would catch every unrelated TypeError in the app.
 *
 * @param {any} err
 * @returns {boolean}
 */
export function isStaleChunkError(err) {
  const message = String((err && (err.message || err)) || '')
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /^Loading chunk \S+ failed/i.test(message)
  )
}

/**
 * Should we reload, given what has already been tried?
 *
 * Pure, so the loop guard can be tested without a browser that reloads.
 *
 * @param {{last?: unknown, now?: number, window?: number}} state
 * @returns {boolean}
 */
export function shouldReload(state = {}) {
  const now = Number.isFinite(state.now) ? Number(state.now) : Date.now()
  const window = Number.isFinite(state.window) ? Number(state.window) : RELOAD_WINDOW_MS
  const last = Number(state.last)
  if (!Number.isFinite(last) || last <= 0) return true
  /* A clock that jumped backwards must not permanently disable the recovery. */
  if (last > now) return true
  return now - last > window
}

/**
 * Handle a route failure. Returns true if a reload was started.
 *
 * @param {any} err
 * @param {{
 *   storage?: Storage, reload?: () => void, now?: () => number, log?: (...args: any[]) => void,
 * }} [deps]
 * @returns {boolean}
 */
export function recoverFromStaleBundle(err, deps = {}) {
  if (!isStaleChunkError(err)) return false

  const now = deps.now || (() => Date.now())
  const log = deps.log || console.warn
  const reload = deps.reload || (() => globalThis.location.reload())

  let storage = deps.storage
  if (storage === undefined) {
    try {
      storage = globalThis.sessionStorage
    } catch {
      storage = undefined
    }
  }

  let last = 0
  try {
    last = Number(storage && storage.getItem(RELOAD_KEY)) || 0
  } catch {
    last = 0
  }

  if (!shouldReload({ last, now: now() })) {
    log('stale bundle: already reloaded once, not looping', err)
    return false
  }

  try {
    if (storage) storage.setItem(RELOAD_KEY, String(now()))
  } catch {
    /* Private mode. Reload anyway — one refresh is better than a dead app,
       and without storage we cannot loop-guard, so this is the one case where
       the window is not enforced. It is also the case where sessionStorage
       being unavailable means the page is unlikely to be a long-lived one. */
  }

  log('stale bundle detected after a deploy; reloading once', err)
  reload()
  return true
}
